// src/pages/VehicleManagement.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Truck, Plus, Search, Edit2, Trash2, AlertCircle,
  Calendar, Wrench, MapPin, Activity, Menu, Settings,
  ChevronRight, ChevronDown, Tag, Package, X, Star, ExternalLink
} from 'lucide-react';
import { 
  fetchVehicles, 
  addVehicle, 
  updateVehicle, 
  deleteVehicle 
} from '../store/slices/vehicleSlice';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import { fetchNotifications } from '../store/slices/notificationSlice';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getLoadDisplayId, getLoadUrlId } from '../utils/loadUtils';

const VehicleManagement = () => {
  const dispatch = useDispatch();
  const { items: vehicles, status, error, pagination } = useSelector(state => state.vehicles);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [trailerModalOpen, setTrailerModalOpen] = useState(false);
  const [selectedVehicleForTrailers, setSelectedVehicleForTrailers] = useState(null);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);

  useEffect(() => {
    dispatch(fetchVehicles({ page: currentPage, search: searchTerm }));
  }, [dispatch, currentPage, searchTerm]);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  // Add global event listener for vehicle/trailer updates
  useEffect(() => {
    const handleVehicleUpdate = () => {
      // Refresh vehicles when updates happen from other components
      dispatch(fetchVehicles({ page: currentPage, search: searchTerm }));
    };

    // Listen for custom events that indicate vehicle/trailer updates
    window.addEventListener('vehicleUpdated', handleVehicleUpdate);
    window.addEventListener('trailerUpdated', handleVehicleUpdate);

    return () => {
      window.removeEventListener('vehicleUpdated', handleVehicleUpdate);
      window.removeEventListener('trailerUpdated', handleVehicleUpdate);
    };
  }, [dispatch, currentPage, searchTerm]);

  const handleAddVehicle = () => {
    setSelectedVehicle(null);
    setIsModalOpen(true);
  };

  const handleEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsModalOpen(true);
  };

  const handleDeleteVehicle = async (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle? This will also delete all associated trailers.')) {
      await dispatch(deleteVehicle(id));
      dispatch(fetchVehicles({ page: currentPage, search: searchTerm }));
    }
  };

  const handleManageTrailers = (vehicle) => {
    setSelectedVehicleForTrailers(vehicle);
    setTrailerModalOpen(true);
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <SideNavigation isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      
      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0 px-2' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className="max-w-7xl mx-auto py-4 sm:py-6 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4 px-2">
            {(isMobile || isTablet) && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 bg-white rounded-md shadow-md"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 ml-2">
              Fleet Management
            </h1>
            <button
              onClick={handleAddVehicle}
              className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-5 w-5 sm:mr-2" />
              <span className="hidden sm:inline">Add Vehicle</span>
            </button>
          </div>

          {/* Mobile-optimized Search */}
          <div className="mb-4 sm:mb-6 px-2">
            <form className="flex items-center">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder="Search vehicles, tractors, trailers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </form>
          </div>

          {/* Responsive Vehicle Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 px-2">
            <AnimatePresence>
              {vehicles.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  onEdit={() => handleEditVehicle(vehicle)}
                  onDelete={() => handleDeleteVehicle(vehicle.id)}
                  onManageTrailers={() => handleManageTrailers(vehicle)}
                  isMobile={isMobile}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Vehicle Modal */}
          <VehicleModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            vehicle={selectedVehicle}
            currentPage={currentPage}
            searchTerm={searchTerm}
            pagination={pagination}
            isMobile={isMobile}
          />

          {/* Trailer Management Modal */}
          <TrailerManagementModal
            isOpen={trailerModalOpen}
            onClose={() => setTrailerModalOpen(false)}
            vehicle={selectedVehicleForTrailers}
            onUpdate={() => {
              dispatch(fetchVehicles({ page: currentPage, search: searchTerm }));
            }}
            isMobile={isMobile}
          />
        </div>
      </main>
    </div>
  );
};

const VehicleCard = ({ vehicle, onEdit, onDelete, onManageTrailers, isMobile }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  
  const statusColors = {
    available: 'bg-green-100 text-green-800',
    in_use: 'bg-blue-100 text-blue-800',
    maintenance: 'bg-yellow-100 text-yellow-800'
  };

  const primaryTrailer = vehicle.trailers?.find(t => t.is_primary);
  const trailerCount = vehicle.trailer_count || 0;
  const currentLoad = vehicle.current_load;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-4 sm:p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Truck className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {vehicle.year} {vehicle.type}
              </h3>
              <div className="text-xs sm:text-sm text-gray-500">
                {vehicle.tractor_number && (
                  <p>Tractor: {vehicle.tractor_number}</p>
                )}
                {vehicle.make && vehicle.model && (
                  <p>{vehicle.make} {vehicle.model}</p>
                )}
              </div>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[vehicle.status]}`}>
            {vehicle.status?.replace('_', ' ').toUpperCase()}
          </span>
        </div>

        {/* Current Load Badge - Show when vehicle is in use */}
        {currentLoad && vehicle.status === 'in_use' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4"
          >
            <button
              onClick={() => navigate(`/loads/${getLoadUrlId(currentLoad)}`)}
              className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-indigo-50 via-purple-50 to-blue-50 
                        border-2 border-indigo-200 rounded-xl hover:border-indigo-300 hover:shadow-md 
                        transition-all duration-200 group"
            >
              <div className="p-2 bg-indigo-500 rounded-lg group-hover:bg-indigo-600 transition-colors">
                <Package className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-indigo-900">Load #{getLoadDisplayId(currentLoad)}</span>
                  <span className="px-1.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
                    {currentLoad.status?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-indigo-700 mt-0.5 truncate">
                  {currentLoad.origin_city}, {currentLoad.origin_state} → {currentLoad.destination_city}, {currentLoad.destination_state}
                </p>
              </div>
              <ExternalLink className="h-4 w-4 text-indigo-400 group-hover:text-indigo-600 transition-colors" />
            </button>
          </motion.div>
        )}

        {/* Trailer Summary */}
        {trailerCount > 0 && (
          <div className="mb-4">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center w-full text-left p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 shadow-sm"
            >
              <div className="p-1.5 bg-blue-600 rounded-lg mr-3">
                <Package className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <span className="text-sm font-semibold text-blue-900">
                  {trailerCount} Trailer{trailerCount !== 1 ? 's' : ''}
                </span>
                {primaryTrailer && (
                  <p className="text-xs text-blue-700 mt-0.5">
                    Primary: {primaryTrailer.trailer_number}
                  </p>
                )}
              </div>
              <motion.div
                animate={{ rotate: expanded ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronRight className="h-5 w-5 text-blue-600" />
              </motion.div>
            </button>
            
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 space-y-2 px-1">
                    {vehicle.trailers?.map((trailer) => (
                      <motion.div
                        key={trailer.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                          trailer.is_primary 
                            ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200' 
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`p-1.5 rounded-lg ${
                            trailer.is_primary ? 'bg-yellow-100' : 'bg-gray-100'
                          }`}>
                            <Package className={`h-3 w-3 ${
                              trailer.is_primary ? 'text-yellow-600' : 'text-gray-600'
                            }`} />
                          </div>
                          <div>
                            <span className="font-semibold text-gray-900 text-sm">
                              {trailer.trailer_number}
                            </span>
                            {trailer.is_primary && (
                              <div className="flex items-center mt-1">
                                <Star className="h-3 w-3 text-yellow-500 mr-1" />
                                <span className="text-xs font-bold text-yellow-700">PRIMARY</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          trailer.status === 'available' 
                            ? 'bg-green-50 text-green-700 border-green-200' :
                          trailer.status === 'in_use' 
                            ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            'bg-yellow-50 text-yellow-700 border-yellow-200'
                        }`}>
                          {trailer.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Compact info rows for mobile */}
        <div className="space-y-2 sm:space-y-3">
          <InfoRow icon={MapPin} label="Location" value={`${vehicle.current_location?.city || 'N/A'}`} isMobile={isMobile} />
          {vehicle.last_maintenance_date && (
            <InfoRow icon={Wrench} label="Maintenance" value={new Date(vehicle.last_maintenance_date).toLocaleDateString()} isMobile={isMobile} />
          )}
          {vehicle.plate_number && (
            <InfoRow icon={Activity} label="Plate" value={vehicle.plate_number} isMobile={isMobile} />
          )}
          {vehicle.capacity && (
            <InfoRow icon={Activity} label="Capacity" value={`${vehicle.capacity} tons`} isMobile={isMobile} />
          )}
        </div>

        {/* Mobile-optimized action buttons */}
        <div className="mt-4 sm:mt-6 flex flex-wrap gap-2">
          <button
            onClick={onEdit}
            className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Edit2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Edit</span>
          </button>
          
          <button
            onClick={onManageTrailers}
            className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50"
          >
            <Settings className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Trailers</span>
          </button>
          
          <button
            onClick={onDelete}
            className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Delete</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const InfoRow = ({ icon: Icon, label, value, isMobile }) => (
  <div className="flex items-center text-xs sm:text-sm">
    <Icon className="h-4 w-4 text-gray-400 mr-2" />
    <span className="text-gray-500">{isMobile ? label : `${label}:`}</span>
    <span className="ml-2 font-medium text-gray-900">{value}</span>
  </div>
);

const VehicleModal = ({ 
  isOpen, 
  onClose, 
  vehicle, 
  currentPage,
  searchTerm,
  pagination,
  isMobile
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    type: '',
    make: '',
    model: '',
    year: '',
    vin: '',
    tractor_number: '',
    plate_number: '',
    capacity: '',
    status: 'available',
    // Single trailer for backward compatibility
    trailer_number: ''
  });
  const [formSection, setFormSection] = useState('primary');

  useEffect(() => {
    if (vehicle) {
      setFormData({
        ...vehicle,
        // For backward compatibility, include primary trailer number
        trailer_number: vehicle.primary_trailer?.trailer_number || ''
      });
    } else {
      setFormData({
        type: '',
        make: '',
        model: '',
        year: '',
        vin: '',
        tractor_number: '',
        plate_number: '',
        capacity: '',
        status: 'available',
        trailer_number: ''
      });
    }
  }, [vehicle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (vehicle) {
        await dispatch(updateVehicle({ id: vehicle.id, data: formData }));
      } else {
        await dispatch(addVehicle(formData));
      }
      dispatch(fetchVehicles({ 
        page: currentPage, 
        search: searchTerm,
        perPage: pagination.perPage 
      }));
      onClose();
      toast.success(vehicle ? 'Vehicle updated successfully' : 'Vehicle added successfully');
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Failed to save vehicle');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div 
              className="w-full max-w-2xl rounded-xl bg-white shadow-2xl"
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-4">
                <div className="mb-6">
                  <div className="flex space-x-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => setFormSection('primary')}
                      className={`px-4 py-2 text-sm font-medium ${
                        formSection === 'primary' 
                          ? 'text-blue-600 border-b-2 border-blue-600' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Vehicle Information
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormSection('details')}
                      className={`px-4 py-2 text-sm font-medium ${
                        formSection === 'details' 
                          ? 'text-blue-600 border-b-2 border-blue-600' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Additional Details
                    </button>
                  </div>
                </div>

                {formSection === 'primary' && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FormField
                        label="Vehicle Type"
                        name="type"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        required
                        placeholder="e.g., Semi-truck, Delivery Van"
                      />
                      <FormField
                        label="Year"
                        name="year"
                        type="number"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        required
                        placeholder="e.g., 2023"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FormField
                        label="Tractor Number"
                        name="tractor_number"
                        value={formData.tractor_number}
                        onChange={(e) => setFormData({ ...formData, tractor_number: e.target.value })}
                        icon={Truck}
                        placeholder="e.g., T-001"
                        helpText="Optional - Used for identification"
                      />
                      <FormField
                        label="Primary Trailer Number"
                        name="trailer_number"
                        value={formData.trailer_number}
                        onChange={(e) => setFormData({ ...formData, trailer_number: e.target.value })}
                        icon={Package}
                        placeholder="e.g., TR-001"
                        helpText="Optional - You can add more trailers after creating the vehicle"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FormField
                        label="Plate Number"
                        name="plate_number"
                        value={formData.plate_number}
                        onChange={(e) => setFormData({ ...formData, plate_number: e.target.value })}
                        placeholder="e.g., ABC-1234"
                        helpText="Optional - Vehicle license plate"
                      />
                    
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status <span className="text-xs text-gray-500">(Required)</span>
                        </label>
                        <div className="relative">
                          <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="appearance-none block w-full rounded-md border-gray-300 shadow-sm 
                                      focus:border-blue-500 focus:ring-blue-500 pl-3 pr-10 py-2"
                          >
                            <option value="available">Available</option>
                            <option value="in_use">In Use</option>
                            <option value="maintenance">Maintenance</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Current operational status</p>
                      </div>
                    </div>
                  </div>
                )}

                {formSection === 'details' && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FormField
                        label="Make"
                        name="make"
                        value={formData.make}
                        onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                        placeholder="e.g., Peterbilt, Freightliner"
                        helpText="Optional - Vehicle manufacturer"
                      />
                      <FormField
                        label="Model"
                        name="model"
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        placeholder="e.g., 579, Cascadia"
                        helpText="Optional - Vehicle model"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FormField
                        label="VIN"
                        name="vin"
                        value={formData.vin}
                        onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                        placeholder="17-digit VIN number"
                        helpText="Optional - Vehicle identification number"
                      />
                      <FormField
                        label="Capacity (tons)"
                        name="capacity"
                        type="number"
                        step="0.1"
                        value={formData.capacity}
                        onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                        placeholder="e.g., 26.5"
                        helpText="Optional - Vehicle carrying capacity"
                      />
                    </div>
                  </div>
                )}
              
                <div className="mt-8 flex justify-end space-x-3 border-t border-gray-200 pt-5">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 
                              rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 
                              focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent 
                              rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 
                              focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {vehicle ? 'Update Vehicle' : 'Add Vehicle'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const TrailerManagementModal = ({ isOpen, onClose, vehicle, onUpdate, isMobile }) => {
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingTrailer, setAddingTrailer] = useState(false);
  const [editingTrailer, setEditingTrailer] = useState(null);
  const [formData, setFormData] = useState({
    trailer_number: '',
    type: '',
    make: '',
    model: '',
    year: '',
    capacity: '',
    length: '',
    width: '',
    height: '',
    weight: '',
    max_weight: '',
    vin: '',
    license_plate: '',
    status: 'available',
    notes: '',
    is_primary: false
  });

  useEffect(() => {
    if (isOpen && vehicle) {
      fetchTrailers();
    }
  }, [isOpen, vehicle]);

  const fetchTrailers = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/vehicles/${vehicle.id}/trailers`);
      setTrailers(response.data.trailers);
    } catch (error) {
      console.error('Failed to fetch trailers:', error);
      toast.error('Failed to load trailers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrailer = () => {
    setFormData({
      trailer_number: '',
      type: '',
      make: '',
      model: '',
      year: '',
      capacity: '',
      length: '',
      width: '',
      height: '',
      weight: '',
      max_weight: '',
      vin: '',
      license_plate: '',
      status: 'available',
      notes: '',
      is_primary: trailers.length === 0
    });
    setEditingTrailer(null);
    setAddingTrailer(true);
  };

  const handleEditTrailer = (trailer) => {
    setFormData(trailer);
    setEditingTrailer(trailer);
    setAddingTrailer(true);
  };

  const handleSubmitTrailer = async (e) => {
    e.preventDefault();
    try {
      if (editingTrailer) {
        await api.put(`/api/trailers/${editingTrailer.id}`, formData);
        toast.success('Trailer updated successfully');
      } else {
        await api.post(`/api/vehicles/${vehicle.id}/trailers`, formData);
        toast.success('Trailer added successfully');
      }
      setAddingTrailer(false);
      setEditingTrailer(null);
      fetchTrailers();
      onUpdate();
    } catch (error) {
      console.error('Error saving trailer:', error);
      toast.error(error.response?.data?.error || 'Failed to save trailer');
    }
  };

  const handleDeleteTrailer = async (trailerId) => {
    if (window.confirm('Are you sure you want to delete this trailer?')) {
      try {
        await api.delete(`/api/trailers/${trailerId}`);
        toast.success('Trailer deleted successfully');
        fetchTrailers();
        onUpdate();
      } catch (error) {
        console.error('Error deleting trailer:', error);
        toast.error('Failed to delete trailer');
      }
    }
  };

  const handleSetPrimary = async (trailerId) => {
    try {
      await api.put(`/api/trailers/${trailerId}/set-primary`);
      toast.success('Primary trailer updated');
      fetchTrailers();
      onUpdate();
    } catch (error) {
      console.error('Error setting primary trailer:', error);
      toast.error('Failed to set primary trailer');
    }
  };

  if (!isOpen || !vehicle) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div 
              className="w-full max-w-4xl rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-hidden"
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      Trailer Management
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {vehicle.year} {vehicle.type} - {vehicle.tractor_number || 'N/A'}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                {addingTrailer ? (
                  <form onSubmit={handleSubmitTrailer} className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900">
                        {editingTrailer ? 'Edit Trailer' : 'Add New Trailer'}
                      </h4>
                      <button
                        type="button"
                        onClick={() => setAddingTrailer(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        label="Trailer Number"
                        name="trailer_number"
                        value={formData.trailer_number}
                        onChange={(e) => setFormData({ ...formData, trailer_number: e.target.value })}
                        required
                        placeholder="e.g., TR-001"
                        helpText="Required - Unique identifier for this trailer"
                      />
                      <FormField
                        label="Type"
                        name="type"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        placeholder="e.g., Dry Van, Reefer, Flatbed"
                        helpText="Optional - Trailer type or classification"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <FormField
                        label="Make"
                        name="make"
                        value={formData.make}
                        onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                        placeholder="e.g., Wabash"
                        helpText="Optional"
                      />
                      <FormField
                        label="Model"
                        name="model"
                        value={formData.model}
                        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        placeholder="e.g., DuraPlate"
                        helpText="Optional"
                      />
                      <FormField
                        label="Year"
                        name="year"
                        type="number"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        placeholder="e.g., 2023"
                        helpText="Optional"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        label="Capacity (tons)"
                        name="capacity"
                        type="number"
                        step="0.1"
                        value={formData.capacity}
                        onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                        placeholder="e.g., 26.5"
                        helpText="Optional - Weight capacity"
                      />
                      <FormField
                        label="Length (ft)"
                        name="length"
                        type="number"
                        step="0.1"
                        value={formData.length}
                        onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                        placeholder="e.g., 53"
                        helpText="Optional - Trailer length"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        label="VIN"
                        name="vin"
                        value={formData.vin}
                        onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                        placeholder="17-digit VIN"
                        helpText="Optional - Vehicle identification number"
                      />
                      <FormField
                        label="License Plate"
                        name="license_plate"
                        value={formData.license_plate}
                        onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                        placeholder="e.g., TR123456"
                        helpText="Optional - Trailer license plate"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="available">Available</option>
                          <option value="in_use">In Use</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="out_of_service">Out of Service</option>
                        </select>
                      </div>
                      <div className="flex items-center pt-6">
                        <input
                          type="checkbox"
                          id="is_primary"
                          checked={formData.is_primary}
                          onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="is_primary" className="ml-2 text-sm text-gray-700">
                          Set as primary trailer
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Additional notes about this trailer..."
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setAddingTrailer(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                      >
                        {editingTrailer ? 'Update Trailer' : 'Add Trailer'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-lg font-medium text-gray-900">
                        Trailers ({trailers.length})
                      </h4>
                      <button
                        onClick={handleAddTrailer}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Trailer
                      </button>
                    </div>

                    {loading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : trailers.length === 0 ? (
                      <div className="text-center py-8">
                        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h5 className="text-lg font-medium text-gray-900 mb-2">No trailers assigned</h5>
                        <p className="text-gray-500 mb-4">Add the first trailer for this vehicle.</p>
                        <button
                          onClick={handleAddTrailer}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Trailer
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {trailers.map((trailer) => (
                          <motion.div
                            key={trailer.id}
                            layout
                            className={`p-4 rounded-lg border-2 transition-all ${
                              trailer.is_primary ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <h5 className="text-lg font-medium text-gray-900">
                                    {trailer.trailer_number}
                                  </h5>
                                  {trailer.is_primary && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      Primary
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    trailer.status === 'available' ? 'bg-green-100 text-green-800' :
                                    trailer.status === 'in_use' ? 'bg-blue-100 text-blue-800' :
                                    trailer.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {trailer.status.replace('_', ' ')}
                                  </span>
                                </div>
                                <div className="mt-2 text-sm text-gray-600 grid grid-cols-2 md:grid-cols-4 gap-4">
                                  {trailer.type && <div><span className="font-medium">Type:</span> {trailer.type}</div>}
                                  {trailer.capacity && <div><span className="font-medium">Capacity:</span> {trailer.capacity} tons</div>}
                                  {trailer.length && <div><span className="font-medium">Length:</span> {trailer.length} ft</div>}
                                  {trailer.make && trailer.model && <div><span className="font-medium">Make/Model:</span> {trailer.make} {trailer.model}</div>}
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                {!trailer.is_primary && (
                                  <button
                                    onClick={() => handleSetPrimary(trailer.id)}
                                    className="inline-flex items-center px-3 py-1.5 border border-blue-300 rounded text-xs font-medium text-blue-700 bg-white hover:bg-blue-50"
                                  >
                                    <Tag className="h-3 w-3 mr-1" />
                                    Set Primary
                                  </button>
                                )}
                                <button
                                  onClick={() => handleEditTrailer(trailer)}
                                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  <Edit2 className="h-3 w-3 mr-1" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteTrailer(trailer.id)}
                                  className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded text-xs font-medium text-red-700 bg-white hover:bg-red-50"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const FormField = ({ label, name, type = "text", value, onChange, icon: Icon, required = false, placeholder, helpText, step }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative rounded-md shadow-sm">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
      )}
      <input
        type={type}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        step={step}
        className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 
                  focus:ring-blue-500 sm:text-sm ${Icon ? 'pl-10' : ''}`}
      />
    </div>
    {helpText && (
      <p className="mt-1 text-xs text-gray-500">{helpText}</p>
    )}
  </div>
);

export default VehicleManagement;